import { useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";

/**
 * Where the "Weekly summary" section sits relative to "Dump Detections".
 * `after` (the default) keeps the report's information hierarchy — evidence
 * first, then the roll-up numbers. `before` lets a reviewer lead with the
 * headline KPIs instead. Kept as a small string union (not a boolean) so the
 * dropdown option labels map 1:1 and the intent reads at the call site.
 */
export type WeeklySummaryPosition = "after" | "before";

/**
 * How the "Dump Detections" section presents its dumps. `list` (the default)
 * is the stacked-card layout; `carousel` pages through one dump at a time via
 * the shared `MediaCarousel`. A string union (not a boolean) so option labels
 * map 1:1 and future modes (e.g. "grid") slot in without a rename.
 */
export type DumpDetectionsView = "list" | "carousel";

/**
 * Where the dump metadata (Object / Detected / Site / Description) sits in the
 * carousel, relative to the clip and the thumbnail strip:
 *   - `below-clip` (default): clip → meta → thumbnails
 *   - `bottom`:               clip → thumbnails → meta
 *   - `top`:                  meta → clip → thumbnails
 * Carousel-only — the list view has no separate meta placement. A string union
 * (not an index) so the dropdown labels map 1:1 and the intent reads at the
 * call site.
 */
export type DumpMetaPosition = "below-clip" | "bottom" | "top";

interface WasteSenseControlsProps {
  weeklySummaryPosition: WeeklySummaryPosition;
  onWeeklySummaryPositionChange: (value: WeeklySummaryPosition) => void;
  dumpDetectionsView: DumpDetectionsView;
  onDumpDetectionsViewChange: (value: DumpDetectionsView) => void;
  dumpMetaPosition: DumpMetaPosition;
  onDumpMetaPositionChange: (value: DumpMetaPosition) => void;
}

const WEEKLY_SUMMARY_OPTIONS: readonly { value: WeeklySummaryPosition; label: string }[] = [
  { value: "after", label: "After Detected Dumps" },
  { value: "before", label: "Before Detected Dumps" },
];

const DUMP_DETECTIONS_OPTIONS: readonly { value: DumpDetectionsView; label: string }[] = [
  { value: "list", label: "List" },
  { value: "carousel", label: "Carousel" },
];

const DUMP_META_POSITION_OPTIONS: readonly { value: DumpMetaPosition; label: string }[] = [
  { value: "below-clip", label: "Below clip" },
  { value: "bottom", label: "Bottom" },
  { value: "top", label: "Top" },
];

/**
 * Report "Control" section — presentation toggles that live at the very bottom
 * of the report, below Sites. Web-only (raw <select>): the whole WasteSense
 * report is gated to web, and a native HTML select is the simplest accessible
 * control for a small, fixed option set — no outside-click handling or portal
 * needed, unlike the app's custom dropdowns.
 */
export function WasteSenseControls({
  weeklySummaryPosition,
  onWeeklySummaryPositionChange,
  dumpDetectionsView,
  onDumpDetectionsViewChange,
  dumpMetaPosition,
  onDumpMetaPositionChange,
}: WasteSenseControlsProps) {
  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Control
      </Text>
      <View flexDirection="row" flexWrap="wrap" gap="3xl">
        <ControlSelect
          label="Detected Dumps"
          value={dumpDetectionsView}
          options={DUMP_DETECTIONS_OPTIONS}
          onChange={onDumpDetectionsViewChange}
        />
        {/* Meta placement only applies to the carousel view — the list card has
            a fixed layout — so the control appears only when carousel is active
            to avoid offering a toggle that would do nothing. */}
        {dumpDetectionsView === "carousel" ?
          <ControlSelect
            label="Metadata Position"
            value={dumpMetaPosition}
            options={DUMP_META_POSITION_OPTIONS}
            onChange={onDumpMetaPositionChange}
          />
        : null}
        <ControlSelect
          label="Weekly Summary"
          value={weeklySummaryPosition}
          options={WEEKLY_SUMMARY_OPTIONS}
          onChange={onWeeklySummaryPositionChange}
        />
      </View>
    </View>
  );
}

interface ControlSelectProps<V extends string> {
  label: string;
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (value: V) => void;
}

/**
 * A single labelled control: an uppercase label above a themed native
 * <select>. Web-only (raw <select>) — the whole WasteSense report is gated to
 * web, and a native select is the simplest accessible control for a small,
 * fixed option set (no outside-click handling or portal, unlike the app's
 * custom dropdowns).
 */
function ControlSelect<V extends string>({ label, value, options, onChange }: ControlSelectProps<V>) {
  const { theme } = useUnistyles();

  return (
    <View gap="xs" alignItems="flex-start">
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {label}
      </Text>
      {/* Raw <select> styled with theme tokens so it matches the report's
          surface/border/text. `all: unset` strips the UA chrome; we re-apply
          only what we need. */}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as V)}
        style={{
          all: "unset",
          boxSizing: "border-box",
          minWidth: 220,
          paddingTop: theme.spacing.lg,
          paddingBottom: theme.spacing.lg,
          paddingLeft: theme.spacing["3xl"],
          paddingRight: theme.spacing["3xl"],
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: theme.colors.border as string,
          borderRadius: sizes.radius,
          backgroundColor: theme.colors.surface as string,
          color: theme.colors.text as string,
          cursor: "pointer",
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </View>
  );
}
